/**
 * Avatar Configuration
 *
 * Avatar collection organized by categories.
 * Each avatar has a unique ID, name, path, and category.
 */

export interface Avatar {
  id: string
  name: string
  description: string
  path: string
  color: string
  category: string
  isPremium?: boolean
}

export interface AvatarCategory {
  id: string
  name: string
  description: string
  icon?: string
  isPremium?: boolean
}

// Avatar Categories
export const avatarCategories: AvatarCategory[] = [
  {
    id: 'one-piece',
    name: 'One Piece',
    description: 'Avatares inspirados en el anime One Piece',
    icon: '🏴‍☠️',
  },
  {
    id: 'premium',
    name: 'Premium',
    description: 'Avatares exclusivos (Próximamente)',
    icon: '⭐',
    isPremium: true,
  },
]

// All Avatars
export const avatars: Avatar[] = [
  // One Piece Collection
  {
    id: 'captain-straw',
    name: 'Monkey D. Luffy',
    description: 'El futuro rey de los piratas',
    path: '/avatars/one-piece/captain-straw.svg',
    color: '#3A8AD6',
    category: 'one-piece',
  },
  {
    id: 'green-swordsman',
    name: 'Roronoa Zoro',
    description: 'El mejor espadachín del mundo',
    path: '/avatars/one-piece/green-swordsman.svg',
    color: '#1D4A3A',
    category: 'one-piece',
  },
  {
    id: 'orange-navigator',
    name: 'Nami',
    description: 'La mejor navegante de los mares',
    path: '/avatars/one-piece/orange-navigator.svg',
    color: '#3C9FD0',
    category: 'one-piece',
  },
  {
    id: 'blonde-chef',
    name: 'Sanji',
    description: 'El cocinero de los sueños',
    path: '/avatars/one-piece/blonde-chef.svg',
    color: '#28406E',
    category: 'one-piece',
  },
  {
    id: 'cute-doctor',
    name: 'Tony Tony Chopper',
    description: 'El pequeño médico de emergencias',
    path: '/avatars/one-piece/cute-doctor.svg',
    color: '#3AA892',
    category: 'one-piece',
  },
  {
    id: 'dark-scholar',
    name: 'Nico Robin',
    description: 'La arqueóloga de los secretos',
    path: '/avatars/one-piece/dark-scholar.svg',
    color: '#7656AD',
    category: 'one-piece',
  },
  {
    id: 'super-mechanic',
    name: 'Franky',
    description: 'El carpintero cyborg',
    path: '/avatars/one-piece/super-mechanic.svg',
    color: '#CF4436',
    category: 'one-piece',
  },
  {
    id: 'soul-musician',
    name: 'Brook',
    description: 'El esqueleto melódico',
    path: '/avatars/one-piece/soul-musician.svg',
    color: '#D99E2B',
    category: 'one-piece',
  },
  {
    id: 'brave-sniper',
    name: 'Usopp',
    description: 'El guerrero de los mares',
    path: '/avatars/one-piece/brave-sniper.svg',
    color: '#7FA23E',
    category: 'one-piece',
  },
  {
    id: 'wise-helmsman',
    name: 'Jinbe',
    description: 'El caballero del mar',
    path: '/avatars/one-piece/wise-helmsman.svg',
    color: '#E08E2E',
    category: 'one-piece',
  },
]

/**
 * Get avatars by category
 */
export function getAvatarsByCategory(categoryId: string): Avatar[] {
  return avatars.filter((a) => a.category === categoryId)
}

/**
 * Get avatar by ID
 */
export function getAvatarById(id: string | null | undefined): Avatar | undefined {
  if (!id) return undefined
  return avatars.find((a) => a.id === id)
}

/**
 * Get default avatar (captain-straw)
 */
export function getDefaultAvatar(): Avatar {
  return avatars[0]
}

/**
 * Get avatar path, with fallback to default
 */
export function getAvatarPath(id: string | null | undefined): string {
  const avatar = getAvatarById(id)
  return avatar?.path || getDefaultAvatar().path
}

/**
 * Get category by ID
 */
export function getCategoryById(id: string): AvatarCategory | undefined {
  return avatarCategories.find((c) => c.id === id)
}
